import type { FunnelParams, FunnelParamsPatch } from '@/model/types';
import { authQuery, writeHeaders } from './apiConfig';
import { PersistenceError, type PersistencePort } from './types';

/**
 * Talks to the separate API service.
 *
 *   GET  {base}/api/scenarios/{id}/params  -> 200 {params, version, updatedAt} | 404
 *   PUT  {base}/api/scenarios/{id}/params  <- {params}
 *   POST {base}/api/scenarios/{id}/params  <- {params}   (the teardown flush)
 *
 * A sparse response is safe: `mergeParams` fills every key the server omits from
 * code defaults, so partial overrides are the expected shape. A 404 means
 * "nothing stored" and maps to null, which the store reads as "use defaults".
 */

export function createHttpAdapter(baseUrl: string): PersistencePort {
  const base = baseUrl.replace(/\/+$/, '');
  const url = (scenarioId: string) =>
    `${base}/api/scenarios/${encodeURIComponent(scenarioId)}/params`;

  return {
    kind: 'http',

    async load(scenarioId) {
      let response: Response;
      try {
        response = await fetch(url(scenarioId), {
          headers: { Accept: 'application/json' },
          credentials: 'omit',
        });
      } catch (cause) {
        throw new PersistenceError('Could not reach the scenario service', cause);
      }

      // 404 is the service answering: there is no scenario for this code. That
      // is a fact the caller can act on, unlike a 5xx or a dead socket.
      if (response.status === 404) return { status: 'absent' };
      if (!response.ok) {
        throw new PersistenceError(`Scenario service returned ${response.status}`);
      }

      const body = (await response.json()) as { params?: FunnelParamsPatch };
      return body.params ? { status: 'found', params: body.params } : { status: 'absent' };
    },

    async save(scenarioId, params) {
      let response: Response;
      try {
        response = await fetch(url(scenarioId), {
          method: 'PUT',
          headers: writeHeaders(),
          credentials: 'omit',
          body: JSON.stringify({ params }),
        });
      } catch (cause) {
        throw new PersistenceError('Could not reach the scenario service', cause);
      }
      if (!response.ok) {
        throw new PersistenceError(`Scenario service returned ${response.status}`);
      }
    },

    flush(scenarioId, params) {
      flushParams(url(scenarioId), params);
    },
  };
}

/**
 * The pagehide write. Two things make this different from `save`.
 *
 * **It is a POST.** `navigator.sendBeacon` can only issue POST, so the service
 * accepts POST alongside PUT on the same route. Without that, every teardown
 * flush would 405 and a session's last edits would silently never land.
 *
 * **It is `text/plain`.** `application/json` is not a CORS-safelisted content
 * type, so it forces a preflight — during page teardown, when there may not be
 * time for the extra round trip. A safelisted type makes this a simple request
 * that always goes out. The body is still JSON; only the label differs, and the
 * service registers a text/plain parser for exactly this reason.
 *
 * Because headers are unavailable here, the key and session id ride in the query
 * string instead.
 */
function flushParams(endpoint: string, params: FunnelParams): void {
  const target = `${endpoint}${authQuery()}`;
  const payload = JSON.stringify({ params });
  const type = 'text/plain;charset=UTF-8';

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    // Queued by the browser and sent even after the document is gone.
    if (navigator.sendBeacon(target, new Blob([payload], { type }))) return;
    // A false return means the payload was refused (queue full). Fall through.
  }

  void fetch(target, {
    method: 'POST',
    headers: { 'Content-Type': type },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * Falls back to a second port when the primary throws. Keeps a meeting alive if
 * the API service is unreachable: edits still persist locally.
 */
export function withFallback(primary: PersistencePort, fallback: PersistencePort): PersistencePort {
  return {
    kind: primary.kind,
    async load(scenarioId) {
      try {
        return await primary.load(scenarioId);
      } catch {
        /* The primary could not be reached. Anything the fallback holds is
           better than nothing — but if it holds nothing either, the answer is
           `unavailable`, not `absent`. Reporting absence here would let an API
           outage masquerade as "unknown code" and lock a live meeting out. */
        const local = await fallback.load(scenarioId);
        return local.status === 'found' ? local : { status: 'unavailable' };
      }
    },
    async save(scenarioId, params) {
      try {
        await primary.save(scenarioId, params);
      } catch (error) {
        await fallback.save(scenarioId, params);
        throw error;
      }
    },
    flush(scenarioId, params) {
      primary.flush(scenarioId, params);
      fallback.flush(scenarioId, params);
    },
  };
}
